/**
 * User Service
 *
 * API calls for user management
 */

import { api } from '../lib/api';

export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING_PROFILE';
export type AuthMethod = 'GOOGLE' | 'LINKEDIN' | 'PHONE_OTP' | 'EMAIL_OTP';

export interface UserListItem {
  id: string;
  email: string;
  phone: string | null;
  name: string | null;
  companyName: string | null;
  designation: string | null;
  authMethod: AuthMethod;
  profilePicture: string | null;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  _count: {
    inquiries: number;
    wishlists: number;
    catalogueDownloads: number;
  };
}

export interface UserDetails {
  user: {
    id: string;
    email: string;
    phone: string | null;
    name: string | null;
    companyName: string | null;
    designation: string | null;
    authMethod: AuthMethod;
    profilePicture: string | null;
    status: UserStatus;
    createdAt: string;
    updatedAt: string;
  };
  activity: {
    inquiryCount: number;
    wishlistCount: number;
    catalogueDownloads: number;
  };
  recentInquiries: Array<{
    id: string;
    referenceNo: string;
    type: string;
    status: string;
    createdAt: string;
  }>;
  recentDownloads: Array<{
    id: string;
    categoryName: string;
    downloadedAt: string;
  }>;
}

export interface UserListOptions {
  page?: number;
  limit?: number;
  status?: UserStatus;
  search?: string;
  authMethod?: AuthMethod;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export const userService = {
  list: async (options: UserListOptions = {}): Promise<PaginatedResponse<UserListItem>> => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);
    if (options.authMethod) params.append('authMethod', options.authMethod);

    const response = await api.get<UserListItem[]>(`/admin/users?${params.toString()}`);
    return {
      data: response.data || [],
      meta: response.meta || { page: 1, limit: 20, total: 0, hasMore: false },
    };
  },

  getById: async (id: string): Promise<UserDetails> => {
    const response = await api.get<UserDetails>(`/admin/users/${id}`);
    return response.data!;
  },

  updateStatus: async (id: string, status: 'ACTIVE' | 'INACTIVE'): Promise<UserListItem> => {
    const response = await api.patch<UserListItem>(`/admin/users/${id}/status`, { status });
    return response.data!;
  },
};
